import { useId, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '@safetyhub/ui';
import { adminApi } from '../../lib/api';
import { ModalCancelButton } from '../ModalCancelButton';
import { useToastStore } from '../../stores/toast';

interface GroupCreateModalProps {
  onClose: () => void;
}

export default function GroupCreateModal({ onClose }: GroupCreateModalProps) {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // 첫 포커스는 그룹 이름 — `autoFocus`는 base가 열림 직후 패널로 포커스를 옮기며 덮으므로
  // `initialFocusRef`로 준다.
  const nameRef = useRef<HTMLInputElement>(null);
  // 제출 버튼이 base footer(폼 밖)에 있어 `form` 속성으로 폼에 묶는다 — Enter 제출이 그대로 산다.
  const formId = useId();

  const createMutation = useMutation({
    mutationFn: () =>
      adminApi.createFeatureFlagGroup({ name, description: description || undefined }),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flag-groups'] });
        showToast('그룹이 생성되었습니다.', 'success');
        onClose();
      } else {
        showToast(result.error?.message || '생성에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('그룹 생성 중 오류가 발생했습니다.', 'error');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate();
  };

  const isPending = createMutation.isPending;

  // 부모가 열 때만 마운트하므로 open은 늘 true다. 생성 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다.
  return (
    <Modal
      open
      onClose={onClose}
      title="사용자 그룹 생성"
      size="md"
      initialFocusRef={nameRef}
      isDirty={name !== '' || description !== ''}
      closeOnOverlay={!isPending}
      closeOnEsc={!isPending}
      showCloseButton={!isPending}
      closeLocked={isPending}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} disabled={isPending} />
          <Button type="submit" form={formId} variant="primary" disabled={!name.trim() || isPending}>
            {isPending ? '생성 중...' : '생성'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            그룹 이름 <span className="text-red-500">*</span>
          </label>
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예: 베타 테스터, 내부 QA팀"
            className="w-full px-3 py-2 border border-gray-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="그룹에 대한 설명 (선택사항)"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
          />
        </div>
      </form>
    </Modal>
  );
}
